import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    InputAdornment,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import controlLoopServices from "app/services/automations/control-loop-services";
import {
    DIRECTION_OPTIONS,
    actuatorLabel,
    describeLoopBehavior,
    rangeError,
    rangeFromThresholds,
    thresholdsFromRange,
    variableWithArticle,
} from "app/shared/automations/controlLoopDisplay";
import { errorMessage } from "app/shared/devices/actuatorDisplay";
import AutomationMetaHeader from "../../components/AutomationMetaHeader";
import EditorSaveBar from "../../components/EditorSaveBar";
import RecipientsEditor from "../../components/RecipientsEditor";
import SectionCard from "../../components/SectionCard";

const EMPTY_FORM = {
    id: null,
    name: "",
    description: "",
    notification_group: "",
    enabled: true,
    actuator_device_id: "",
    fail_safe_state: "",
    sensor_device_id: "",
    measurement: "",
    direction: "raise",
    min: "",
    max: "",
    lease_minutes: 15,
    max_on_hours: "",
    recipients: [],
    guards: [],
};

const SAFE_STATE_WORDS = { OFF: "apagada", ON: "encendida" };

const valueOrBlank = (value) => (_.isNil(value) ? "" : value);

// Del lazo que entrega la API (o de un borrador del asistente) al formulario: los umbrales pasan a mínimo y máximo.
const loopToForm = (loop = {}) => {
    const { min, max } = rangeFromThresholds(loop);
    return {
        ...EMPTY_FORM,
        id: loop.id || null,
        name: loop.name || "",
        description: loop.description || "",
        notification_group: loop.notification_group || "",
        enabled: loop.enabled !== false,
        actuator_device_id: valueOrBlank(loop.actuator_device_id),
        fail_safe_state: loop.fail_safe_state || "",
        sensor_device_id: valueOrBlank(loop.sensor_device_id),
        measurement: loop.measurement || "",
        direction: loop.direction || "raise",
        min: valueOrBlank(min),
        max: valueOrBlank(max),
        lease_minutes: valueOrBlank(loop.lease_minutes) || 15,
        max_on_hours: valueOrBlank(loop.max_on_hours),
        recipients: (loop.recipients || []).map((recipient) => _.pick(recipient, ["id", "user_id", "channel", "enabled"])),
        guards: loop.guards || [],
    };
};

const draftPayload = (message) => {
    const payload = message?.draft_payload || {};
    return { payload, loop: payload.control_loop || payload };
};

// Editor de un control «Mantener en rango», contado como una frase: qué quieres mantener, entre qué valores, con qué
// equipo, qué pasa si algo falla y a quién avisar. Las salidas ya tomadas por otra automatización se ven con la razón.
const ControlLoopEditor = ({ id, duplicateFromId, query = {}, assistantDraft, groupOptions = [], onSaved, onCancel, setAssistantContext }) => {
    const navigate = useNavigate();
    const [options, setOptions] = useState(null);
    const [form, setForm] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // Al guardar se vuelve a la lista: el editor ya no existe cuando termina la petición.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const draftTargetId = assistantDraft ? (draftPayload(assistantDraft).payload.mode === "update" ? draftPayload(assistantDraft).payload.target_id : null) : null;
    const editingId = id || draftTargetId || null;

    useEffect(() => {
        let active = true;
        const sourceId = editingId || duplicateFromId;

        Promise.all([
            controlLoopServices.getEditOptions({ controlLoopId: editingId }),
            sourceId ? controlLoopServices.getControlLoop(sourceId) : Promise.resolve(null),
        ])
            .then(([editOptions, loop]) => {
                if (!active) return;
                setOptions(editOptions || {});

                if (assistantDraft) {
                    const { loop: draft } = draftPayload(assistantDraft);
                    const fromDraft = _.omitBy(loopToForm(draft), (value, key) => key === "id" || value === "" || _.isNil(value));
                    setForm({ ...loopToForm(loop || {}), ...fromDraft, id: loop?.id || null });
                } else if (id && loop) {
                    setForm(loopToForm(loop));
                    setShowAdvanced(!!loop.max_on_hours || !!loop.guards?.length);
                } else if (duplicateFromId && loop) {
                    // Otra salida: la del original ya tiene su control.
                    setForm({ ...loopToForm(loop), id: null, name: `Copia de ${loop.name}`, enabled: false, actuator_device_id: "", fail_safe_state: "" });
                } else {
                    setForm({ ...EMPTY_FORM, actuator_device_id: valueOrBlank(query.actuator_id) });
                }
            })
            .catch(() => { if (active) setLoadError("No se pudo cargar el control."); });

        return () => { active = false; };
    }, [assistantDraft, duplicateFromId, editingId, id, query.actuator_id]);

    useEffect(() => {
        if (!form) return;
        setAssistantContext?.({
            mode: "form",
            current_control_loop: {
                id: form.id,
                name: form.name,
                actuator_device_id: form.actuator_device_id || null,
                sensor_device_id: form.sensor_device_id || null,
                measurement: form.measurement || null,
                direction: form.direction,
                ...thresholdsFromRange(form),
                enabled: form.enabled,
            },
        });
    }, [form, setAssistantContext]);

    const actuators = useMemo(() => options?.actuators || [], [options]);
    const sensors = useMemo(() => options?.sensors || [], [options]);
    const lease = options?.lease_minutes || { min: 5, max: 120, default: 15 };
    const selectedActuator = _.find(actuators, (actuator) => `${actuator.id}` === `${form?.actuator_device_id}`);
    const selectedSensor = _.find(sensors, (sensor) => `${sensor.id}` === `${form?.sensor_device_id}`);
    const selectedVariable = _.find(selectedSensor?.variables, { field: form?.measurement });
    const unit = selectedVariable?.unit;
    const variableName = variableWithArticle(selectedVariable);
    const needsFailSafe = !!selectedActuator && !selectedActuator.supervised;
    const safeState = needsFailSafe ? form?.fail_safe_state : selectedActuator?.fail_safe_state;
    const rangeProblem = form ? rangeError(form) : null;
    const leaseMinutes = Number(form?.lease_minutes);
    const leaseError = form && (!Number.isFinite(leaseMinutes) || leaseMinutes < lease.min || leaseMinutes > lease.max)
        ? `Entre ${lease.min} y ${lease.max} minutos.`
        : null;
    const unitAdornment = unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined;

    const update = (patch) => setForm((current) => ({ ...current, ...patch }));
    const field = (name) => (event) => update({ [name]: event.target.value });

    const canSave = !!form
        && !!form.sensor_device_id
        && !!form.measurement
        && form.min !== "" && form.max !== ""
        && !rangeProblem
        && !!form.actuator_device_id
        && (!needsFailSafe || !!form.fail_safe_state)
        && !leaseError;

    const save = () => {
        if (!canSave) return;
        const thresholds = thresholdsFromRange(form);
        const payload = {
            name: form.name?.trim() || "",
            description: form.description || "",
            notification_group: form.notification_group?.trim() || null,
            enabled: form.enabled !== false,
            sensor_device_id: form.sensor_device_id,
            measurement: form.measurement,
            direction: form.direction,
            on_threshold: Number(thresholds.on_threshold),
            off_threshold: Number(thresholds.off_threshold),
            lease_minutes: leaseMinutes,
            max_on_hours: form.max_on_hours === "" ? "" : Number(form.max_on_hours),
            recipients: (form.recipients || []).filter((recipient) => recipient.user_id)
                .map((recipient) => _.pick(recipient, ["user_id", "channel", "enabled"])),
        };
        if (!form.id) payload.actuator_device_id = form.actuator_device_id;
        if (needsFailSafe) payload.fail_safe_state = form.fail_safe_state;

        setSaving(true);
        setSaveError(null);
        const request = form.id
            ? controlLoopServices.updateControlLoop(form.id, payload)
            : controlLoopServices.createControlLoop(payload);
        request
            .then((saved) => onSaved?.(saved))
            .catch((error) => { if (mountedRef.current) setSaveError(errorMessage(error, "No se pudo guardar el control.")); })
            .finally(() => { if (mountedRef.current) setSaving(false); });
    };

    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!form || !options) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const behavior = form.min !== "" && form.max !== "" && !rangeProblem && selectedVariable
        ? describeLoopBehavior({ direction: form.direction, min: form.min, max: form.max, unit, variable: variableName, output: selectedActuator?.name })
        : null;

    return (
        <>
            <Stack spacing={2.25}>
                <AutomationMetaHeader
                    automation={form}
                    onChange={update}
                    onBack={onCancel}
                    groupOptions={groupOptions}
                    namePlaceholder="Nombre del control (opcional)"
                />

                <SectionCard
                    title="Qué quieres mantener"
                    subtitle="La medición de un sensor que este control va a cuidar."
                    icon={<SensorsOutlinedIcon />}
                    accent="primary.main">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Sensor"
                                value={form.sensor_device_id}
                                onChange={(event) => update({ sensor_device_id: event.target.value, measurement: "" })}
                                helperText={sensors.length ? undefined : "No hay sensores con mediciones numéricas."}
                                SelectProps={{ renderValue: () => selectedSensor?.name || "" }}>
                                {sensors.map((sensor) => (
                                    <MenuItem key={`sensor-${sensor.id}`} value={sensor.id}>
                                        <ListItemText primary={sensor.name} secondary={sensor.store_name} />
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Medición"
                                value={form.measurement}
                                onChange={field("measurement")}
                                disabled={!selectedSensor}>
                                {(selectedSensor?.variables || []).map((variable) => (
                                    <MenuItem key={`variable-${variable.field}`} value={variable.field}>
                                        {variable.label}{variable.unit ? ` (${variable.unit})` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    title="Entre qué valores"
                    subtitle="El rango que quieres mantener. Dentro de él la salida no cambia, así no se enciende y apaga a cada rato."
                    icon={<TuneOutlinedIcon />}
                    accent="success.main">
                    <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                            <TextField fullWidth type="number" label="Mínimo" value={form.min} onChange={field("min")} InputProps={unitAdornment} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Máximo"
                                value={form.max}
                                onChange={field("max")}
                                error={!!rangeProblem}
                                helperText={rangeProblem || undefined}
                                InputProps={unitAdornment}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    title="Con qué equipo"
                    subtitle="La salida que este control enciende y apaga. Cada salida la maneja un solo control."
                    icon={<PowerSettingsNewOutlinedIcon />}
                    accent="warning.main">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Salida"
                                value={form.actuator_device_id}
                                disabled={!!form.id}
                                onChange={(event) => update({ actuator_device_id: event.target.value, fail_safe_state: "" })}
                                helperText={form.id ? "Para usar otra salida, duplica este control." : (actuators.length ? undefined : "No hay salidas controlables en tu empresa.")}
                                SelectProps={{ renderValue: () => actuatorLabel(selectedActuator) }}>
                                {actuators.map((actuator) => {
                                    const isCurrent = `${actuator.id}` === `${form.actuator_device_id}`;
                                    return (
                                        <MenuItem key={`actuator-${actuator.id}`} value={actuator.id} disabled={!actuator.available && !isCurrent}>
                                            <ListItemText
                                                primary={actuatorLabel(actuator)}
                                                secondary={actuator.available ? actuator.store_name : actuator.unavailable_reason}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label={`Al encenderla, ${variableName}…`}
                                value={form.direction}
                                onChange={field("direction")}>
                                {DIRECTION_OPTIONS.map((option) => (
                                    <MenuItem key={`direction-${option.value}`} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {needsFailSafe && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label="Si algo falla, la salida debe…"
                                    value={form.fail_safe_state}
                                    onChange={field("fail_safe_state")}
                                    helperText="Su estado seguro: lo que hace si el sensor deja de informar o la plataforma no responde. Queda guardado en la salida.">
                                    {(options.fail_safe_states || ["OFF", "ON"]).map((state) => (
                                        <MenuItem key={`fail-safe-${state}`} value={state}>{`Quedar ${SAFE_STATE_WORDS[state] || state}`}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                    {selectedActuator?.supervised && (
                        <Typography variant="body2" color="text.secondary">
                            Si algo falla, esta salida queda <b>{SAFE_STATE_WORDS[selectedActuator.fail_safe_state]}</b> (su estado seguro; se cambia en el panel del equipo).
                        </Typography>
                    )}
                    {selectedActuator?.locked_out && (
                        <Alert severity="warning">El equipo está bloqueado por seguridad: el control no actúa hasta que alguien lo revise y lo libere.</Alert>
                    )}
                    {behavior && <Alert severity="info" icon={false}>{behavior}</Alert>}
                </SectionCard>

                <SectionCard
                    title="Si algo falla"
                    subtitle="Cómo se protege la salida si el control no puede hacer su trabajo."
                    icon={<ShieldOutlinedIcon />}
                    accent="error.main">
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                        <Typography variant="body2">Si el sensor deja de informar por más de</Typography>
                        <TextField
                            size="small"
                            type="number"
                            value={form.lease_minutes}
                            onChange={field("lease_minutes")}
                            error={!!leaseError}
                            inputProps={{ min: lease.min, max: lease.max }}
                            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                            sx={{ width: { xs: "100%", sm: 130 } }}
                        />
                        <Typography variant="body2">
                            la salida queda {safeState ? SAFE_STATE_WORDS[safeState] : "en su estado seguro"} y se avisa.
                        </Typography>
                    </Stack>
                    <Typography variant="caption" color={leaseError ? "error.main" : "text.secondary"}>
                        {leaseError || "Usa unas 3 veces el intervalo con que reporta el sensor."}
                    </Typography>

                    <Box>
                        <Button size="small" sx={{ textTransform: "none", px: 0.5 }}
                                endIcon={showAdvanced ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                                onClick={() => setShowAdvanced((current) => !current)}>
                            Más opciones
                        </Button>
                        <Collapse in={showAdvanced}>
                            <Stack spacing={1.25} sx={{ pt: 1 }}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                    <Typography variant="body2">Si la salida lleva más de</Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={form.max_on_hours}
                                        onChange={field("max_on_hours")}
                                        placeholder="sin límite"
                                        InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                                        sx={{ width: { xs: "100%", sm: 150 } }}
                                    />
                                    <Typography variant="body2">encendida sin parar, apagarla y pausar este control hasta que alguien lo revise.</Typography>
                                </Stack>
                                {!!form.guards?.length && (
                                    <Stack spacing={0.75}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}>Resguardos configurados por Xpit</Typography>
                                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                            {form.guards.map((guard, index) => (
                                                <Chip key={`guard-${index}`} size="small" variant="outlined" label={guard.label || `${guard.measurement} ${guard.operator} ${guard.value}`} />
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </Collapse>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        ¿Necesitas un tope, por ejemplo que la salida nunca se encienda sobre cierta temperatura?{" "}
                        <Button size="small" sx={{ textTransform: "none", p: 0, minWidth: 0, verticalAlign: "baseline" }}
                                onClick={() => navigate(`/automations/rules/new/safety_limit${form.actuator_device_id ? `?actuator_id=${form.actuator_device_id}` : ""}`)}>
                            Crea un límite de seguridad
                        </Button>
                    </Typography>
                </SectionCard>

                <SectionCard
                    title="A quién avisar"
                    subtitle="Cuando la salida queda en su estado seguro porque algo falló, o cuando el equipo se bloquea."
                    icon={<NotificationsActiveOutlinedIcon />}
                    accent="primary.main">
                    <RecipientsEditor
                        recipients={form.recipients}
                        onChange={(recipients) => update({ recipients })}
                        users={options.users || []}
                        channels={options.channels || []}
                    />
                </SectionCard>

                {!!saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
                {!canSave && (
                    <Alert severity="info">
                        Elige el sensor y la medición, el mínimo y el máximo, y la salida{needsFailSafe ? " con lo que debe hacer si algo falla" : ""} para guardar el control.
                    </Alert>
                )}
            </Stack>
            <EditorSaveBar saving={saving} disabled={!canSave} onSave={save} label="Guardar control" />
        </>
    );
};

export default ControlLoopEditor;
